/**
 * Журнал мероприятия: всё, что случилось, одной лентой и по нажатию.
 *
 * Владелец: «нужно собрать все логи всех операций и чтоб кликабельно всё
 * было». До этого они лежали по разным экранам — закрытие магазина в событиях,
 * копия у агентов, разворот в своей карточке, правка зоны в доменах. Каждый
 * экран знал свою часть, и целого не знал никто, а мероприятие целое и есть.
 *
 * Строка, за которой стоят подробности, открывает их прямо здесь. Не ссылкой
 * на другую вкладку: в ночь переезда уход с экрана — это потеря места, где ты
 * находишься, а вернуться к нужной строке потом стоит дороже, чем кажется.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { agoWords } from '../lib/rate.ts';
import { RestoreReport } from './RestoreReport.tsx';

interface Entry {
  at: string;
  level: string;
  type: string;
  msg: string;
  link: { kind: 'restore' | 'dns' | 'run'; id: string } | null;
  /** Сколько раз подряд повторилось. 1 — обычная строка. */
  repeats?: number;
}

/** Как называется работа, породившая строку. Короче типа и понятнее. */
const WORD: Record<string, string> = {
  'site.maintenance.on': 'закрытие',
  'site.maintenance.off': 'открытие',
  'site.maintenance.done': 'магазин',
  'agent.run-requested': 'копия',
  'agent.snapshot': 'снимок',
  'agent.run-failed': 'копия',
  'restore.started': 'разворот',
  'restore.finished': 'разворот',
  'restore.failed': 'разворот',
  'restore.interrupted': 'разворот',
  'restore.applied': 'развёртывание',
  'restore.repaired': 'проверка',
  'restore.net': 'сеть',
  'restore.links': 'ссылки',
  'restore.xattr': 'атрибуты',
  'restore.owners': 'владельцы',
  'restore.webroot': 'веб-корень',
  'dns.switch': 'домен',
  'dns.rollback': 'домен',
  'dns.ttl': 'домен',
  'site.public.check': 'проверка домена',
  'site.public.progress': 'проверка домена',
  'site.public.done': 'проверка домена',
  'agent.log': 'приёмник',
  'notify.sent': 'письмо',
};

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function CampaignLog({ id }: { id: string }): React.JSX.Element {
  const { data, reload } = useQuery<{ items: Entry[]; since: string; until: string | null }>(
    'campaigns.log', { id },
  );
  const [openRestore, setOpenRestore] = useState<string | null>(null);
  const [onlyImportant, setOnlyImportant] = useState(false);

  const items = data?.items ?? [];
  /*
   * Отсев по важности, а не по типу.
   *
   * В ночь переключения строк набегают сотни, и большая часть — ход работы:
   * полезный, пока смотришь, и мешающий, когда ищешь, что пошло не так.
   * Уровень для этого и заведён.
   */
  const shown = onlyImportant ? items.filter((x) => x.level !== 'info') : items;

  return (
    <section className="wizard__step">
      <h3 className="wizard__title">
        Журнал мероприятия
        <span className="muted"> · {items.length} строк</span>
      </h3>
      <p className="muted">
        Всё, что случилось с начала мероприятия: закрытие магазина, копия,
        разворот, правка зоны, проверка домена. Строки со ссылкой открывают
        подробности по нажатию.
      </p>

      <div className="wizard__row">
        <button type="button" className="button button--ghost button--small"
                onClick={() => setOnlyImportant((v) => !v)}>
          {onlyImportant ? 'показать всё' : 'только важное'}
        </button>
        <button type="button" className="button button--ghost button--small"
                onClick={() => reload()}>
          обновить
        </button>
        {data?.since && (
          <span className="muted">
            с {new Date(data.since).toLocaleString('ru-RU', {
              dateStyle: 'short', timeStyle: 'short',
            })}
            {data.until
              ? ` по ${new Date(data.until).toLocaleString('ru-RU', {
                dateStyle: 'short', timeStyle: 'short' })} — мероприятие закрыто`
              : ' — идёт'}
          </span>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="muted">Пока ничего не случилось.</p>
      ) : (
        <ol className="camplog">
          {shown.map((e, i) => {
            const openable = e.link?.kind === 'restore';
            const Row = openable ? 'button' : 'div';
            return (
              <li key={`${e.at}-${i}`} className={`camplog__row camplog__row--${e.level}`}>
                <Row
                  {...(openable
                    ? {
                      type: 'button' as const,
                      className: 'camplog__hit',
                      onClick: () => setOpenRestore(e.link!.id),
                      title: 'Открыть разбор этой работы',
                    }
                    : { className: 'camplog__hit camplog__hit--flat' })}
                >
                  <span className="camplog__when mono">{clock(e.at)}</span>
                  <span className="camplog__what">{WORD[e.type] ?? e.type}</span>
                  <span className="camplog__msg">
                    {e.msg}
                    {/*
                      Повтор показываем числом, а не двадцатью строками.
                      «Двадцать раз подряд» — это сведение: столько опросов
                      получили один и тот же ответ.
                    */}
                    {(e.repeats ?? 1) > 1 && (
                      <span className="muted"> · ×{e.repeats}</span>
                    )}
                    {openable && <span className="camplog__more"> — разбор →</span>}
                  </span>
                </Row>
              </li>
            );
          })}
        </ol>
      )}

      {items.length > 0 && (
        <p className="muted">
          Последняя запись {agoWords(Date.now() - new Date(items[items.length - 1]!.at).getTime())}.
        </p>
      )}

      {openRestore && (
        <RestoreReport restoreId={openRestore} onClose={() => setOpenRestore(null)} />
      )}
    </section>
  );
}
